/* eslint-disable no-prototype-builtins */
import React, { useContext, useEffect, useRef, useState } from "react";
import { Routes, Route, useParams, useNavigate } from "react-router";
import { Formik, Form, FormikProps } from "formik";
import { styled } from "@mui/material";
import {
  sortSites,
  APPROVE,
  TEST,
  INFORMATION,
  CYCLE,
  PEER_REVIEW,
  PRESCRIPTION,
  SIMULATION,
  VOLUMING,
} from "i18n/constants";
import Information from "./Information";
import Simulation, {
  simulationPageBack,
  simulationPageNext,
} from "./Simulation";
import Completed from "./Completed";
import Prescription, {
  prescriptionPageBack,
  prescriptionPageNext,
} from "./Prescription";
import Voluming, {
  volumingPageBack,
  volumingPageNext,
  volumingPageNextText,
} from "./Voluming";
import Cycle from "./cycle/Cycle";
import PeerReview from "./PeerReview";
import { roValidationSchema, moValidationSchema } from "../validation";
import InformationDefaultValues from "./InformationDefaultValues";
import SideBarNav from "components/SideBarNav/SideBarNav";
import { canUserEdit, getActiveStep } from "shared/utils";
import UserContext from "contexts/UserContext";
import BottomNav from "components/base/BottomNav";
import { StandardPaddedContainer } from "components/CommonStyledComponents";
import { useMutation } from "@apollo/client";
import {
  GET_CAREPLAN_TEMPLATE,
  CLEANUP_TEMPLATE,
} from "graphql/TemplateQueries";
import { useTenantConfig } from "contexts/TenantConfigContext";

interface ParamTypes {
  templateId: string;
  form: string;
  pageId: string;
}

interface Props {
  template: any;
  reloadTemplate: () => any;
  listData: any;
  diagnosisCategoryData: any;
  siteListData: any;
}

const TemplateMainContainer = styled("div")`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  height: calc(100% - 56px);
`;

const TemplateFormContainer = styled("div")`
  flex: 1;
  height: 100%;
  overflow: auto;
`;

const CareplanCreatorForm = ({
  template,
  listData,
  diagnosisCategoryData,
  siteListData,
  reloadTemplate,
}: Props) => {
  const { templateId } = useParams<{ templateId: string }>();
  const params = useParams();
  const pageId = params["*"]?.split("/")[1] || "";
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const { tenantConfig } = useTenantConfig();
  const [valid, setValid] = useState<boolean>();
  const showPeerReview = tenantConfig.peerReview;
  const templateRegions = template?.regions.map(
    (region: { name: string }) => region.name,
  );

  const [cleanupTemplate] = useMutation(CLEANUP_TEMPLATE, {
    refetchQueries: [
      { query: GET_CAREPLAN_TEMPLATE, variables: { templateId } },
    ],
    awaitRefetchQueries: true,
  });
  const siteOptions = siteListData.map((site: { name: string }) => ({
    label: site.name,
    value: site.name,
  }));

  const selectOptions = listData.reduce(
    (categoryOptions: any, categoryOption: any) => {
      const { category, data } = categoryOption;
      const { option } = data;
      categoryOptions[category.category] = !categoryOptions.hasOwnProperty(
        category.category,
      )
        ? [
            {
              label: option,
              value: option,
              listWeight: categoryOption.listWeight,
            },
          ]
        : [
            ...categoryOptions[category.category],
            {
              label: option,
              value: option,
              listWeight: categoryOption.listWeight,
            },
          ];
      return categoryOptions;
    },
    {},
  );
  // Sort the select options by listWeight
  for (const key in selectOptions) {
    selectOptions[key] = selectOptions[key].sort(
      (a: any, b: any) => a.listWeight - b.listWeight,
    );
  }
  const setDisableEditing = () => {
    // Don't allow editing of approved or test templates
    if ([APPROVE, TEST].includes(template.status)) {
      return true;
    }
    // Don't allow editing of archived templates
    if (!template.isActive) {
      return true;
    }
    // Don't allow editing by users from different regions
    if (!canUserEdit(user, template)) {
      return true;
    }
    return false;
  };

  const disableEditing = setDisableEditing();
  const prescriptionTemplates = template.prescriptionTemplate;
  const siteTemplate =
    prescriptionTemplates
      .map((prescriptionTemplate: any) =>
        prescriptionTemplate?.siteGroupTemplate
          ?.map((siteGroup: any) => siteGroup.siteTemplate)
          .flat()
          .sort((a: any, b: any) => Number(a.id) - Number(b.id)),
      )
      .flat() || [];
  const prescriptionIds = prescriptionTemplates.map(
    (prescriptionTemplate: any) => prescriptionTemplate.id,
  );
  const simulationGroupIds = prescriptionTemplates
    .map((prescriptionTemplate: any) =>
      prescriptionTemplate.siteGroupTemplate.map(
        (siteGroupTemplate: any) => siteGroupTemplate.id,
      ),
    )
    .flat();
  const siteIds = siteTemplate.flat().map((site: any) => site?.id);

  const volumingGroupIds = prescriptionTemplates.map(
    (prescriptionTemplate: any) => prescriptionTemplate.id,
  );
  const firstVolumingGroupId = String(volumingGroupIds[0]);
  const lastVolumingGroupId = String(
    volumingGroupIds[volumingGroupIds.length - 1],
  );
  const firstSimulationGroupId = String(simulationGroupIds[0]);
  const lastSimulationGroupId = String(
    simulationGroupIds[simulationGroupIds.length - 1],
  );

  const firstSiteId = String(siteIds[0]);
  const lastSiteId = String(siteIds[siteIds.length - 1]);
  const informationDefaultValues = InformationDefaultValues(template);
  // MO Protocol Template Stuff
  const { isRo } = template;

  const ref = useRef<FormikProps<any>>(null);

  const nextInformationUrl = isRo
    ? `simulation/${firstSimulationGroupId}`
    : "cycle/0";

  const activeStep = getActiveStep();
  const navigationVars: any = {
    [INFORMATION]: {
      nextUrl: `/template/${templateId}/${nextInformationUrl}`,
      backUrl: `/template/${templateId}`,
      disableBackOnError: false,
      disableNextOnError: false,
      nextFunction: () => {
        if (!disableEditing) {
          // only cleanup empty site group templates when editing is enabled and continue to next step
          cleanupTemplate({ variables: { templateId } });
        }
        if (ref.current) {
          ref.current.submitForm().then(() => {
            // Set tumourStreams as touched while maintaining existing touched state
            const formik = ref.current;
            if (formik) {
              const currentTouched = formik.touched || {};
              formik.setTouched({ ...currentTouched, tumourStreams: true });
            }

            ref.current?.validateForm().then((errors) => {
              if (Object.keys(errors).length === 0) {
                // reload the template
                reloadTemplate();
                navigate(`/template/${templateId}/${nextInformationUrl}`);
              }
            });
          });
        }
        if (disableEditing) {
          navigate(`/template/${templateId}/${nextInformationUrl}`);
        }
      },
      backFuction: () => {
        reloadTemplate();
        navigate(`/template/${templateId}`);
      },
    },
    [SIMULATION]: {
      nextUrl: simulationPageNext(
        templateId,
        pageId,
        simulationGroupIds,
        firstSiteId,
      ),
      backUrl: simulationPageBack(templateId, pageId, simulationGroupIds),
    },
    [CYCLE]: {
      nextUrl: "",
      backUrl: `/template/${templateId}/information`,
    },
    [PRESCRIPTION]: {
      nextUrl: prescriptionPageNext(
        templateId,
        pageId,
        firstVolumingGroupId,
        siteIds,
      ),
      backUrl: prescriptionPageBack(
        templateId,
        pageId,
        lastSimulationGroupId,
        siteIds,
      ),
    },
    [VOLUMING]: {
      nextUrl: volumingPageNext(
        templateId,
        pageId,
        prescriptionIds,
        showPeerReview,
      ),
      backUrl: volumingPageBack(
        templateId,
        pageId,
        prescriptionIds,
        lastSiteId,
      ),
      nextLabel: volumingPageNextText(pageId, prescriptionIds, showPeerReview),
    },
    [PEER_REVIEW]: {
      nextUrl: `/template/${templateId}`,
      backUrl: `/template/${templateId}/voluming/${lastVolumingGroupId}`,
      nextLabel: "Template Page",
    },
  };
  useEffect(() => {
    if (template && valid === undefined) {
      const schema = template.isMo ? moValidationSchema : roValidationSchema;
      schema.isValid(informationDefaultValues).then((valid) => {
        setValid(valid);
      });
    }
  }, [template, informationDefaultValues, valid]);

  useEffect(() => {
    if (reloadTemplate) {
      reloadTemplate();
    }
  }, [reloadTemplate, pageId]);
  return (
    <>
      <TemplateMainContainer>
        <SideBarNav
          showPeerReview={showPeerReview}
          valid={disableEditing || Boolean(valid)}
          isRo={isRo}
        />

        <TemplateFormContainer>
          <StandardPaddedContainer>
            <Routes>
              <Route
                path="/information"
                element={
                  <Formik
                    initialValues={informationDefaultValues}
                    validationSchema={
                      template.isMo ? moValidationSchema : roValidationSchema
                    }
                    validateOnChange={false}
                    validateOnBlur={false}
                    innerRef={ref}
                    onSubmit={() => {}}
                    enableReinitialize
                  >
                    <Form>
                      <Information
                        template={template}
                        selectOptions={selectOptions}
                        diagnosisCategoryOptions={diagnosisCategoryData.map(
                          (category: any) => {
                            const { id, name, anatomicSiteNum } = category;
                            return { label: name, value: name, id };
                          },
                        )}
                        siteOptions={siteOptions}
                        disableEditing={disableEditing}
                        setFormValid={(value: boolean) => {
                          setValid(value);
                        }}
                      />
                    </Form>
                  </Formik>
                }
              />
              <Route
                path="/simulation/:groupId"
                element={
                  <Simulation
                    selectOptions={selectOptions}
                    disableEditing={disableEditing}
                    groupIds={simulationGroupIds}
                    templateRegions={templateRegions}
                  />
                }
              />
              <Route
                path="/cycle/:groupId"
                element={
                  <Cycle
                    template={template}
                    selectOptions={selectOptions}
                    disableEditing={disableEditing}
                  />
                }
              />
              <Route
                path="/prescription/:siteId"
                element={
                  <Prescription
                    selectOptions={selectOptions}
                    disableEditing={disableEditing}
                    templateRegions={templateRegions}
                  />
                }
              />
              <Route
                path="/voluming/:groupId"
                element={
                  <Voluming
                    disableEditing={disableEditing}
                    volumingGroupIds={volumingGroupIds}
                  />
                }
              />
              <Route
                path="/peerReview"
                element={
                  <PeerReview
                    template={template}
                    selectOptions={selectOptions}
                    disableEditing={disableEditing}
                  />
                }
              />
            </Routes>
          </StandardPaddedContainer>
        </TemplateFormContainer>
      </TemplateMainContainer>

      <BottomNav
        backUrl={navigationVars[activeStep].backUrl}
        nextUrl={navigationVars[activeStep].nextUrl}
        disableBack={navigationVars[activeStep].disableBackOnError}
        disableNext={navigationVars[activeStep].disableNextOnError}
        nextLabel={navigationVars[activeStep].nextLabel}
        backLabel={navigationVars[activeStep].backLabel}
        nextFunction={navigationVars[activeStep].nextFunction}
        backFunction={navigationVars[activeStep].backFuction}
      />
    </>
  );
};

export default CareplanCreatorForm;
